import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import VideoHero from '@/components/promo/VideoHero';
import SepLine from '@/components/promo/SepLine';
import LetsTalk from '@/components/promo/LetsTalk';
import { promoApi, type NewsPost, type NewsLanguage } from '@/lib/api/promoApi';

export default function News() {
  const { language: paramLang } = useParams<{ language?: string }>();
  const [selectedLang, setSelectedLang] = useState(paramLang || 'all');
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [languages, setLanguages] = useState<NewsLanguage[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchNews = useCallback(async (lang: string) => {
    setLoading(true);
    try {
      const data = await promoApi.getNewsPosts(lang);
      setPosts(data.posts);
      setLanguages(data.languages);
    } catch (err) {
      console.error('[News]', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNews(selectedLang);
  }, [selectedLang, fetchNews]);

  useEffect(() => {
    if (paramLang && paramLang !== selectedLang) {
      setSelectedLang(paramLang);
    }
  }, [paramLang]);

  const handleLangClick = (lang: string) => {
    setSelectedLang(lang);
    window.history.replaceState(null, '', `/news/${lang}`);
  };

  return (
    <div id="news">
      <VideoHero videoSrc="/assets/videos/letstalk.mp4" imageSrc="/assets/images/letstalk.png">
        <h1>NEWS</h1>
      </VideoHero>

      <div className="page-content" style={{ paddingBottom: 0 }}>
        {/* Language filter buttons */}
        {languages.length > 0 && (
          <div className="news-lang-filters">
            {languages.map((lang) => (
              <button
                key={lang.value}
                type="button"
                className={`btn-lang ${lang.value === selectedLang ? 'active' : 'unselected'}`}
                onClick={() => handleLangClick(lang.value)}
              >
                {lang.text}
              </button>
            ))}
          </div>
        )}

        <SepLine />

        {loading ? (
          <p style={{ textAlign: 'center', padding: 60, color: '#999' }}>Loading...</p>
        ) : posts.length === 0 ? (
          <div className="nothing-found">No news found</div>
        ) : (
          <div className="blog-news-grid">
            {posts.map((post, idx) => (
              <div key={post._id || idx} className="blog-post-outer-wrapper">
                <div className="blog-post-wrapper">
                  <a className="link-wrap" href={post.url} target="_blank" rel="noreferrer">
                    <div className="blog-post-image-wrapper">
                      <div className="blog-post-date">{post.publicationDate}</div>
                      {post.mainPicture?.[0]?.url && (
                        <img
                          className="blog-post-image"
                          src={post.mainPicture[0].url}
                          alt={post.title}
                          loading="lazy"
                        />
                      )}
                      <div className="blog-post-title">{post.title}</div>
                    </div>
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        <SepLine invisible />
        <LetsTalk />
      </div>
    </div>
  );
}
